import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, download } from '../lib/api';
import { compact, dateShort, money, money0, pct, qty, thisMonth } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { BarChart, BarList, Empty, ErrorState, Kpi, Loading, useToast } from '../components/ui';

interface SalesReport {
  from: string;
  to: string;
  summary: { net: number; gross: number; discount: number; tax: number; invoices: number };
  byDay: Array<{ day: string; fnb: number; rooms: number; total: number; bills: number }>;
  byMode: Array<{ mode: string; amount: number; invoices: number }>;
  byCategory: Array<{ category: string; quantity: number; revenue: number }>;
  gstSummary: Array<{ gstRate: number; taxable: number; cgst: number; sgst: number; igst: number }>;
}

interface CostReport {
  from: string;
  to: string;
  purchases: number;
  consumption: number;
  wastage: number;
  wastagePct: number;
  fnbSales: number;
  foodCostPct: number;
  grossProfit: number;
  stockValue: number;
  topUsage: Array<{ name: string; unit: string; used: number; value: number }>;
  wastageLines: Array<{
    name: string;
    unit: string;
    quantity: number;
    value: number;
    reason: string | null;
  }>;
}

export function ReportsPage() {
  const [days, setDays] = useState(14);

  const sales = useQuery({
    queryKey: ['reports', 'sales', days],
    queryFn: () => api.get<SalesReport>(`/api/reports/sales?days=${days}`),
  });
  const cost = useQuery({
    queryKey: ['reports', 'cost', days],
    queryFn: () => api.get<CostReport>(`/api/reports/cost?days=${days}`),
  });

  if (sales.isLoading || cost.isLoading) return <Loading label="Crunching the numbers…" />;
  if (sales.error) return <ErrorState error={sales.error} onRetry={() => void sales.refetch()} />;

  const salesData = sales.data!;
  const costData = cost.data!;
  const gstTotal = salesData.gstSummary.reduce(
    (sum, row) => sum + row.cgst + row.sgst + row.igst,
    0,
  );

  return (
    <div className="stack">
      <PageHeader
        title="Reports"
        subtitle={`${dateShort(salesData.from)} → ${dateShort(salesData.to)}`}
        actions={
          <>
            <div className="seg">
              {[7, 14, 30, 90].map((option) => (
                <button
                  key={option}
                  className={option === days ? 'active' : ''}
                  onClick={() => setDays(option)}
                >
                  {option}d
                </button>
              ))}
            </div>
            <button className="btn" onClick={() => window.print()}>
              Print
            </button>
          </>
        }
      />

      <div className="kpis">
        <Kpi
          label="Revenue (net)"
          value={compact(salesData.summary.net)}
          foot={`${salesData.summary.invoices} invoices`}
        />
        <Kpi
          label="Collected (gross)"
          value={compact(salesData.summary.gross)}
          foot="Including GST"
          tone="blue"
        />
        <Kpi
          label="GST liability"
          value={compact(gstTotal)}
          foot="CGST + SGST + IGST"
          tone="amber"
        />
        <Kpi
          label="Food cost"
          value={pct(costData.foodCostPct)}
          foot={`Gross profit ${compact(costData.grossProfit)}`}
          tone={costData.foodCostPct > 38 ? 'red' : ''}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Daily revenue</h2>
          <span className="chip">Net of GST</span>
        </div>
        <div className="card-body">
          <BarChart
            stacked
            height={210}
            data={salesData.byDay.map((day) => ({
              label: dateShort(day.day),
              value: day.fnb,
              stack: day.rooms,
            }))}
          />
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Revenue by category</h2>
          </div>
          <div className="card-body">
            <BarList
              rows={salesData.byCategory.map((row) => ({
                label: `${row.category} · ${row.quantity} sold`,
                value: row.revenue,
              }))}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h2>Settlement mix</h2>
          </div>
          <div className="card-body">
            <BarList
              rows={salesData.byMode.map((row) => ({
                label: `${row.mode} · ${row.invoices} bills`,
                value: row.amount,
              }))}
            />
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>GST summary</h2>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Slab</th>
                  <th className="num">Taxable</th>
                  <th className="num">CGST</th>
                  <th className="num">SGST</th>
                  <th className="num">IGST</th>
                </tr>
              </thead>
              <tbody>
                {salesData.gstSummary.map((row) => (
                  <tr key={row.gstRate}>
                    <td className="b">{row.gstRate}%</td>
                    <td className="num">{money(row.taxable)}</td>
                    <td className="num">{money(row.cgst)}</td>
                    <td className="num">{money(row.sgst)}</td>
                    <td className="num">{money(row.igst)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num">
                    {money(salesData.gstSummary.reduce((s, r) => s + r.taxable, 0))}
                  </td>
                  <td className="num">
                    {money(salesData.gstSummary.reduce((s, r) => s + r.cgst, 0))}
                  </td>
                  <td className="num">
                    {money(salesData.gstSummary.reduce((s, r) => s + r.sgst, 0))}
                  </td>
                  <td className="num">
                    {money(salesData.gstSummary.reduce((s, r) => s + r.igst, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Cost of goods</h2>
          </div>
          <div className="card-body">
            <div className="stat-line">
              <span>F&amp;B net sales</span>
              <b>{money(costData.fnbSales)}</b>
            </div>
            <div className="stat-line">
              <span>Consumption at recipe cost</span>
              <b>{money(costData.consumption)}</b>
            </div>
            <div className="stat-line">
              <span>Wastage</span>
              <b>
                {money(costData.wastage)}{' '}
                <span className="xs muted">({pct(costData.wastagePct)})</span>
              </b>
            </div>
            <div className="stat-line">
              <span>Purchases received</span>
              <b>{money(costData.purchases)}</b>
            </div>
            <div className="stat-line">
              <span>Closing stock value</span>
              <b>{money(costData.stockValue)}</b>
            </div>
            <div className="stat-line">
              <span>
                <b>Gross profit</b>
              </span>
              <b>
                {money(costData.grossProfit)}{' '}
                <span className="xs muted">
                  ({pct(costData.fnbSales ? (costData.grossProfit / costData.fnbSales) * 100 : 0)})
                </span>
              </b>
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Highest consumption</h2>
            <span className="chip">Theoretical, from recipes</span>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Ingredient</th>
                  <th className="num">Used</th>
                  <th className="num">Value</th>
                </tr>
              </thead>
              <tbody>
                {costData.topUsage.map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td className="num">{qty(row.used, row.unit)}</td>
                    <td className="num b">{money(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Wastage detail</h2>
          </div>
          {costData.wastageLines.length === 0 ? (
            <Empty icon="✓" message="No wastage recorded in this period" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Ingredient</th>
                    <th className="num">Quantity</th>
                    <th className="num">Value</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {costData.wastageLines.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td className="num">{qty(row.quantity, row.unit)}</td>
                      <td className="num b">{money(row.value)}</td>
                      <td className="small dim">{row.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <DayWiseReports />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Day-wise orders and bookings                                        */
/* ------------------------------------------------------------------ */

interface OrdersReport {
  from: string;
  to: string;
  days: Array<{
    day: string;
    orders: number;
    covers: number;
    items: number;
    net: number;
    gross: number;
    voided: number;
    averageOrder: number;
  }>;
  byChannel: Array<{ channel: string; orders: number; net: number }>;
  byMode: Array<{ mode: string; orders: number; net: number }>;
  totals: { orders: number; covers: number; net: number; gross: number };
}

interface BookingsReport {
  from: string;
  to: string;
  days: Array<{
    day: string;
    bookings: number;
    roomNights: number;
    guests: number;
    value: number;
    paid: number;
  }>;
  byStatus: Array<{ status: string; count: number }>;
  byRoomType: Array<{ roomType: string; bookings: number; value: number }>;
  totals: { bookings: number; roomNights: number; guests: number; value: number; paid: number };
}

const iso = (offset: number) =>
  new Date(Date.now() + offset * 86_400_000).toISOString().slice(0, 10);

/**
 * Day by day, rather than in total.
 *
 * The report above answers "how much"; this answers "on which day, through
 * which channel, and are they turning up" - which is the question a manager
 * asks on a Monday morning with a rota to write.
 */
function DayWiseReports() {
  const [view, setView] = useState<'orders' | 'bookings'>('orders');
  const [from, setFrom] = useState(iso(-13));
  const [to, setTo] = useState(iso(0));
  const [type, setType] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');

  const orders = useQuery({
    queryKey: ['reports', 'orders', from, to, type, channel],
    queryFn: () =>
      api.get<OrdersReport>(
        `/api/reports/orders?from=${from}&to=${to}` +
          (type ? `&type=${type}` : '') +
          (channel ? `&channel=${channel}` : ''),
      ),
    enabled: view === 'orders',
  });

  const bookings = useQuery({
    queryKey: ['reports', 'bookings', from, to, status],
    queryFn: () =>
      api.get<BookingsReport>(
        `/api/reports/bookings?from=${from}&to=${to}` + (status ? `&status=${status}` : ''),
      ),
    enabled: view === 'bookings',
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="seg">
          <button className={view === 'orders' ? 'active' : ''} onClick={() => setView('orders')}>
            Orders by day
          </button>
          <button
            className={view === 'bookings' ? 'active' : ''}
            onClick={() => setView('bookings')}
          >
            Room bookings by day
          </button>
        </div>
      </div>

      <div className="card-body col gap-16">
        <div className="row gap-6 wrap" style={{ alignItems: 'flex-end' }}>
          <label className="field">
            <span>From</span>
            <input
              className="input"
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="field">
            <span>To</span>
            <input
              className="input"
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>

          {view === 'orders' ? (
            <>
              <label className="field">
                <span>Type</span>
                <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">Any</option>
                  <option value="DINE_IN">Dine-in</option>
                  <option value="TAKEAWAY">Takeaway</option>
                  <option value="DELIVERY">Delivery</option>
                </select>
              </label>
              <label className="field">
                <span>Channel</span>
                <select
                  className="select"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="IN_HOUSE">In house</option>
                  <option value="WEB">QR menu</option>
                  <option value="SWIGGY">Swiggy</option>
                  <option value="ZOMATO">Zomato</option>
                  <option value="PHONE">Phone</option>
                </select>
              </label>
            </>
          ) : (
            <label className="field">
              <span>Status</span>
              <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Any</option>
                <option value="HELD">Held</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="CHECKED_IN">Checked in</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </label>
          )}
        </div>

        {view === 'orders' ? (
          orders.isLoading ? (
            <Loading />
          ) : (orders.data?.days.length ?? 0) === 0 ? (
            <Empty message="No orders in that window" />
          ) : (
            <>
              <div className="kpis">
                <Kpi label="Orders" value={orders.data!.totals.orders} foot="In the window" />
                <Kpi label="Covers" value={orders.data!.totals.covers} foot="Guests served" />
                <Kpi
                  label="Net sales"
                  value={money0(orders.data!.totals.net)}
                  foot={`${money0(orders.data!.totals.gross)} with tax`}
                  tone="blue"
                />
              </div>

              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th className="num">Orders</th>
                      <th className="num">Covers</th>
                      <th className="num">Items</th>
                      <th className="num">Average</th>
                      <th className="num">Net</th>
                      <th className="num">With tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.data!.days.map((row) => (
                      <tr key={row.day}>
                        <td>{dateShort(row.day)}</td>
                        <td className="num">{row.orders}</td>
                        <td className="num">{row.covers}</td>
                        <td className="num">{qty(row.items)}</td>
                        <td className="num">{money0(row.averageOrder)}</td>
                        <td className="num b">{money(row.net)}</td>
                        <td className="num dim">{money(row.gross)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {orders.data!.byChannel.length > 0 ? (
                <BarList
                  rows={orders.data!.byChannel.map((row) => ({
                    label: `${row.channel} · ${row.orders}`,
                    value: row.net,
                  }))}
                />
              ) : null}
            </>
          )
        ) : bookings.isLoading ? (
          <Loading />
        ) : (bookings.data?.days.length ?? 0) === 0 ? (
          <Empty message="No bookings arriving in that window" />
        ) : (
          <>
            <div className="kpis">
              <Kpi label="Bookings" value={bookings.data!.totals.bookings} foot="Arriving" />
              <Kpi
                label="Room nights"
                value={bookings.data!.totals.roomNights}
                foot={`${bookings.data!.totals.guests} guest${bookings.data!.totals.guests === 1 ? '' : 's'}`}
              />
              <Kpi
                label="Value"
                value={money0(bookings.data!.totals.value)}
                foot={`${money0(bookings.data!.totals.paid)} paid`}
                tone="blue"
              />
            </div>

            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Arriving</th>
                    <th className="num">Bookings</th>
                    <th className="num">Room nights</th>
                    <th className="num">Guests</th>
                    <th className="num">Value</th>
                    <th className="num">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.data!.days.map((row) => (
                    <tr key={row.day}>
                      <td>{dateShort(row.day)}</td>
                      <td className="num">{row.bookings}</td>
                      <td className="num">{row.roomNights}</td>
                      <td className="num">{row.guests}</td>
                      <td className="num b">{money(row.value)}</td>
                      <td className="num dim">{money(row.paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {bookings.data!.byRoomType.length > 0 ? (
              <BarList
                rows={bookings.data!.byRoomType.map((row) => ({
                  label: `${row.roomType} · ${row.bookings}`,
                  value: row.value,
                }))}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* GST filing                                                          */
/* ------------------------------------------------------------------ */

interface GstReturn {
  period: string;
  supplier: { gstin: string; legalName: string; stateCode: string };
  b2b: Array<{
    gstin: string;
    invoiceNumber: string;
    invoiceDate: string;
    invoiceValue: number;
    placeOfSupply: string;
    items: Array<{
      gstRate: number;
      taxableValue: number;
      cgst: number;
      sgst: number;
      igst: number;
    }>;
  }>;
  b2cs: Array<{
    placeOfSupply: string;
    gstRate: number;
    taxableValue: number;
    cgst: number;
    sgst: number;
    igst: number;
  }>;
  hsn: Array<{
    hsn: string;
    description: string;
    uqc: string;
    quantity: number;
    taxableValue: number;
    cgst: number;
    sgst: number;
    igst: number;
    gstRate: number;
  }>;
  totals: {
    invoices: number;
    taxableValue: number;
    cgst: number;
    sgst: number;
    igst: number;
    total: number;
  };
}

export function GstPage() {
  const { fail, push } = useToast();
  const [period, setPeriod] = useState(thisMonth());
  const [busy, setBusy] = useState(false);

  const gst = useQuery({
    queryKey: ['gst', period],
    queryFn: () => api.get<GstReturn>(`/api/gst/return?period=${period}`),
  });

  const exportCsv = async (section: 'ALL' | 'B2B' | 'B2C' | 'HSN') => {
    setBusy(true);
    try {
      await download(
        `/api/gst/return?period=${period}&format=csv&section=${section}`,
        `gstr1-${period}-${section.toLowerCase()}.csv`,
      );
      push('Export downloaded', 'ok');
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  if (gst.isLoading) return <Loading label="Assembling the return…" />;
  if (gst.error) return <ErrorState error={gst.error} onRetry={() => void gst.refetch()} />;

  const data = gst.data!;

  return (
    <div className="stack">
      <PageHeader
        title="GST Filing"
        subtitle={`GSTR-1 extract for ${data.supplier.legalName} · GSTIN ${data.supplier.gstin}`}
        actions={
          <>
            <input
              className="input"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ width: 160 }}
            />
            <button className="btn" disabled={busy} onClick={() => void exportCsv('B2B')}>
              B2B CSV
            </button>
            <button className="btn" disabled={busy} onClick={() => void exportCsv('HSN')}>
              HSN CSV
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void exportCsv('ALL')}>
              Download full return
            </button>
          </>
        }
      />

      <div className="kpis">
        <Kpi label="Invoices" value={data.totals.invoices} foot={`Period ${data.period}`} />
        <Kpi label="Taxable value" value={compact(data.totals.taxableValue)} foot="Excluding GST" />
        <Kpi
          label="Tax payable"
          value={compact(data.totals.cgst + data.totals.sgst + data.totals.igst)}
          foot={`CGST ${money0(data.totals.cgst)} · SGST ${money0(data.totals.sgst)} · IGST ${money0(data.totals.igst)}`}
          tone="amber"
        />
        <Kpi
          label="Invoice value"
          value={compact(data.totals.total)}
          foot="Including GST and round-off"
          tone="blue"
        />
      </div>

      <div className="readonly-note">
        Void invoices are excluded. This extract is built from the invoice ledger; file it through
        your GSP or the GST portal.
      </div>

      <div className="card">
        <div className="card-head">
          <h2>B2B — registered buyers</h2>
          <span className="chip">{data.b2b.length} invoice(s)</span>
        </div>
        {data.b2b.length === 0 ? (
          <Empty message="No invoices to registered buyers in this period" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Buyer GSTIN</th>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Place of supply</th>
                  <th className="num">Rate</th>
                  <th className="num">Taxable</th>
                  <th className="num">Tax</th>
                  <th className="num">Invoice value</th>
                </tr>
              </thead>
              <tbody>
                {data.b2b.flatMap((invoice) =>
                  invoice.items.map((item, index) => (
                    <tr key={`${invoice.invoiceNumber}-${index}`}>
                      <td className="mono">{invoice.gstin}</td>
                      <td className="b mono">{invoice.invoiceNumber}</td>
                      <td>{dateShort(invoice.invoiceDate)}</td>
                      <td>{invoice.placeOfSupply}</td>
                      <td className="num">{item.gstRate}%</td>
                      <td className="num">{money(item.taxableValue)}</td>
                      <td className="num">{money(item.cgst + item.sgst + item.igst)}</td>
                      <td className="num b">{money(invoice.invoiceValue)}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>B2CS — unregistered, by rate</h2>
          </div>
          {data.b2cs.length === 0 ? (
            <Empty message="No B2C sales in this period" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Place of supply</th>
                    <th className="num">Rate</th>
                    <th className="num">Taxable</th>
                    <th className="num">CGST</th>
                    <th className="num">SGST</th>
                  </tr>
                </thead>
                <tbody>
                  {data.b2cs.map((row) => (
                    <tr key={`${row.placeOfSupply}-${row.gstRate}`}>
                      <td>{row.placeOfSupply}</td>
                      <td className="num">{row.gstRate}%</td>
                      <td className="num b">{money(row.taxableValue)}</td>
                      <td className="num">{money(row.cgst)}</td>
                      <td className="num">{money(row.sgst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>HSN summary</h2>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>HSN/SAC</th>
                  <th>Description</th>
                  <th className="num">Qty</th>
                  <th className="num">Taxable</th>
                  <th className="num">Tax</th>
                </tr>
              </thead>
              <tbody>
                {data.hsn.map((row) => (
                  <tr key={`${row.hsn}-${row.gstRate}`}>
                    <td className="mono b">{row.hsn}</td>
                    <td className="small">{row.description}</td>
                    <td className="num">{row.quantity}</td>
                    <td className="num">{money(row.taxableValue)}</td>
                    <td className="num">{money(row.cgst + row.sgst + row.igst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
